import { useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import type { Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, ArrowLeft, ArrowRight, FileSearch, Lock } from "lucide-react";
import type { NicheConfig } from "../../types/niche";
import { emptySaldo, isSaldoField, saldoSchema, saldoStep1Schema } from "../../lib/saldoSchema";
import type { SaldoFormData } from "../../lib/saldoSchema";
import { FormFieldControl } from "./FormFieldControl";
import { InstitutionGuideLink } from "./InstitutionGuideLink";
import type { FormReturnState } from "./ContractForm";

interface Props {
  config: NicheConfig;
  defaultValues?: SaldoFormData;
  returnState?: FormReturnState | null;
  onSubmit: (data: SaldoFormData) => void;
}

const STEP_TITLES = { 1: "Dados do Bloqueio", 2: "Qualificação do Titular" } as const;

export function SaldoForm({ config, defaultValues, returnState, onSubmit }: Props) {
  const [step, setStep] = useState<1 | 2>(returnState?.step ?? 1);
  const [notice, setNotice] = useState<string | null>(returnState?.notice ?? null);
  const stepRef = useRef(step);
  stepRef.current = step;

  const resolver = useMemo<Resolver<SaldoFormData>>(() => {
    const r1 = zodResolver(saldoStep1Schema);
    const r2 = zodResolver(saldoSchema);
    return (values, ctx, opts) => (stepRef.current === 1 ? r1 : r2)(values, ctx, opts);
  }, []);

  const { control, handleSubmit, watch } = useForm<SaldoFormData>({
    resolver,
    defaultValues: defaultValues ?? emptySaldo,
    mode: "onTouched",
  });
  const values = watch();

  const onStep1 = () => {
    setNotice(null);
    setStep(2);
  };
  const onFinal = (data: SaldoFormData) => {
    setNotice(null);
    onSubmit(data);
  };

  const fields = config.formFields
    .filter((f) => {
      if (f.step !== step) return false;
      if (!f.dependsOn || !isSaldoField(f.dependsOn.field)) return true;
      return values[f.dependsOn.field] === f.dependsOn.value;
    })
    .map((f) =>
      f.type === "document"
        ? { ...f, label: values.tipoTitular === "PF" ? "CPF" : "CNPJ", placeholder: values.tipoTitular === "PF" ? "000.000.000-00" : "00.000.000/0000-00" }
        : f,
    );

  return (
    <form noValidate onSubmit={handleSubmit(step === 1 ? onStep1 : onFinal)} data-testid="saldo-form" className="fade-up">
      <div className="mb-8 flex flex-wrap items-baseline justify-between gap-2">
        <h3 data-testid="saldo-form-step-title" className="text-lg font-semibold text-slate-100">{STEP_TITLES[step]}</h3>
        <p data-testid="saldo-form-step-indicator" className="font-mono text-xs uppercase tracking-[0.16em] text-slate-500">Etapa {step} de 2</p>
      </div>
      <div key={step} className="fade-up grid gap-6 sm:grid-cols-2">
        {fields.map((f) =>
          isSaldoField(f.name) ? (
            <FormFieldControl
              key={f.name}
              field={f}
              name={f.name}
              control={control}
              hint={f.name === "instituicao" ? <InstitutionGuideLink instituicao={values.instituicao} /> : undefined}
            />
          ) : null,
        )}
      </div>
      {notice && (
        <p role="alert" data-testid="saldo-form-notice" className="mt-8 flex gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
          <AlertCircle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />{notice}
        </p>
      )}
      <div className="mt-10 flex flex-col-reverse gap-3 border-t border-slate-800 pt-8 sm:flex-row sm:items-center sm:justify-between">
        {step === 2 ? (
          <button type="button" onClick={() => setStep(1)} data-testid="saldo-form-back-button" className="btn-secondary">
            <ArrowLeft aria-hidden="true" className="h-4 w-4" />Voltar
          </button>
        ) : (
          <p className="flex items-center gap-2 text-xs text-slate-500">
            <Lock aria-hidden="true" className="h-3.5 w-3.5 text-emerald-500" />Dados tratados de forma efêmera, conforme a LGPD.
          </p>
        )}
        {step === 1 ? (
          <button type="submit" data-testid="saldo-form-continue-button" className="btn-primary">
            Continuar<ArrowRight aria-hidden="true" className="h-4 w-4" />
          </button>
        ) : (
          <button type="submit" data-testid="saldo-form-submit-button" className="btn-primary">
            <FileSearch aria-hidden="true" className="h-4 w-4" />Gerar Prévia
          </button>
        )}
      </div>
    </form>
  );
}
